// Power Factor Correction calculation engine (prototype).
// Source: "Power Factor Correction New.xlsm" — reworked where the workbook's math is wrong. See
// sustainability-pros/power-factor/powerfactor-notes.md for the review and the deviations:
//  - PF is the field-measured value (the workbook derived it from hand-entered triangle sides).
//  - Load kW is held constant when PF is corrected; current and kVA fall upstream of the capacitor.
//    (The workbook held amps constant, so kW rose and the "unused power" got priced as energy.)
//  - Required kVAR = kW × (tan φ1 − tan φ2), computed directly (no lookup table, no typos).
//  - Energy savings are only the upstream I²R loss reduction; demand savings need a tariff input.

export type Phase = 1 | 3;

export interface PFMotorInput {
  tag: string;
  location?: string;
  hp?: number;            // nameplate, optional — drives the capacitor-vs-VFD hint
  voltage: number;        // line-to-line for 3-phase
  phase: Phase;
  amps: number;           // measured running current
  measuredPf: number;     // measured displacement PF, 0–1 lagging
  targetPf?: number;      // overrides the project target
  hoursPerDay: number;
  daysPerYear: number;
  installedCost?: number; // $ for capacitor + labour, optional — enables payback
  notes?: string;
}

export interface PFSettings {
  targetPf: number;           // default correction target
  ratePerKwh: number;         // $/kWh
  upstreamLossPct: number;    // % of load lost in feeders/transformer upstream of the capacitor
  demandRatePerKva: number;   // $/kVA-month; 0 = tariff doesn't bill kVA (placeholder until tariff known)
  frequencyHz: number;
  vfdThresholdHp: number;     // author's rule of thumb: above this, a VFD is the better choice
}

export const DEFAULT_PF_SETTINGS: PFSettings = {
  targetPf: 0.95,
  ratePerKwh: 0.10,
  upstreamLossPct: 2,
  demandRatePerKva: 0,
  frequencyHz: 60,
  vfdThresholdHp: 5,
};

/* Standard fixed-capacitor ratings (kVAR). Matches the Grainger/WEG 240/480 V range shown in the
   2026-10-21 walkthrough at the low end; larger sizes are common catalog steps. */
export const STANDARD_KVAR = [
  0.5, 0.75, 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10, 12.5, 15, 20, 25, 30, 40, 50, 60, 75, 100,
];

export const tanPhi = (pf: number) => Math.sqrt(1 - pf * pf) / pf;

/** kW-multiplier from the workbook's "Capacitor Factors" table, computed exactly. */
export const kvarFactor = (fromPf: number, toPf: number) =>
  toPf <= fromPf ? 0 : tanPhi(fromPf) - tanPhi(toPf);

export const nextStandardKvar = (kvar: number): number | null => {
  if (kvar <= 0) return 0;
  return STANDARD_KVAR.find(s => s >= kvar - 1e-9) ?? null;
};

const phaseK = (phase: Phase) => (phase === 3 ? Math.sqrt(3) : 1);
const pfFrom = (kw: number, kvar: number) => (kw <= 0 ? 0 : kw / Math.hypot(kw, kvar));

export type PFMethod = 'capacitor' | 'vfd' | 'none';

export interface PFMotorResult {
  valid: boolean;
  warnings: string[];
  targetPf: number;
  kw: number;
  kvaBefore: number;
  kvarBefore: number;
  kvarNeeded: number;          // exact, to reach target
  capacitorKvar: number | null; // next standard size (null = above catalog range)
  pfAfter: number;             // achieved with the standard size
  leading: boolean;            // standard size over-corrects past unity
  kvaAfter: number;
  kvarAfter: number;
  ampsAfter: number;
  microfaradsWye: number;      // per phase, wye bank (also the 1-phase value)
  microfaradsDelta: number;    // per phase, delta bank
  lossReductionPct: number;    // of upstream I²R losses
  annualKwh: number;
  energySavingsPerYear: number;
  paybackYears: number | null;
  method: PFMethod;
}

export function calcMotor(m: PFMotorInput, s: PFSettings): PFMotorResult {
  const warnings: string[] = [];
  const targetPf = m.targetPf ?? s.targetPf;
  const pf1 = m.measuredPf;
  const valid = m.voltage > 0 && m.amps > 0 && pf1 > 0 && pf1 <= 1 && targetPf > 0 && targetPf <= 1;
  const blank: PFMotorResult = {
    valid: false, warnings, targetPf, kw: 0, kvaBefore: 0, kvarBefore: 0, kvarNeeded: 0,
    capacitorKvar: 0, pfAfter: 0, leading: false, kvaAfter: 0, kvarAfter: 0, ampsAfter: 0,
    microfaradsWye: 0, microfaradsDelta: 0, lossReductionPct: 0, annualKwh: 0,
    energySavingsPerYear: 0, paybackYears: null, method: 'none',
  };
  if (!valid) {
    warnings.push('Enter voltage, amps and a measured PF between 0 and 1.');
    return blank;
  }

  const kvaBefore = (m.voltage * m.amps * phaseK(m.phase)) / 1000;
  const kw = kvaBefore * pf1;
  const kvarBefore = Math.sqrt(Math.max(kvaBefore ** 2 - kw ** 2, 0));
  const kvarNeeded = kw * kvarFactor(pf1, targetPf);

  if (pf1 >= targetPf) warnings.push('Already at or above the target PF — no correction needed.');
  if (pf1 < 0.5) warnings.push('Measured PF below 0.50 — verify the reading before sizing.');

  const capacitorKvar = nextStandardKvar(kvarNeeded);
  if (capacitorKvar === null) warnings.push('Required kVAR exceeds the standard fixed-capacitor range — consider a bank.');
  const appliedKvar = capacitorKvar ?? kvarNeeded;
  const kvarAfterSigned = kvarBefore - appliedKvar;
  const leading = kvarAfterSigned < -1e-9;
  if (leading) warnings.push('Standard size over-corrects to a leading PF — size down or verify no-load kVAR.');
  const kvarAfter = Math.abs(kvarAfterSigned);
  const pfAfter = pfFrom(kw, kvarAfter);
  const kvaAfter = Math.hypot(kw, kvarAfter);
  const ampsAfter = (kvaAfter * 1000) / (m.voltage * phaseK(m.phase));

  // C = Q / (2πf V²). Wye: Q/3 per phase at V/√3 → Q/(ωV_LL²). Delta: Q/3 per phase at V_LL → Q/(3ωV_LL²).
  const omegaV2 = 2 * Math.PI * s.frequencyHz * m.voltage ** 2;
  const microfaradsWye = ((appliedKvar * 1000) / omegaV2) * 1e6;
  const microfaradsDelta = m.phase === 3 ? microfaradsWye / 3 : microfaradsWye;

  const lossReduction = pfAfter > 0 ? Math.max(1 - (pf1 / pfAfter) ** 2, 0) : 0;
  const hours = m.hoursPerDay * m.daysPerYear;
  const annualKwh = kw * hours;
  const energySavingsPerYear = annualKwh * (s.upstreamLossPct / 100) * lossReduction * s.ratePerKwh;

  let method: PFMethod = kvarNeeded > 0 ? 'capacitor' : 'none';
  if (method === 'capacitor' && (m.hp ?? 0) > s.vfdThresholdHp) method = 'vfd';

  const paybackYears = m.installedCost && energySavingsPerYear > 0
    ? m.installedCost / energySavingsPerYear : null;

  return {
    valid, warnings, targetPf, kw, kvaBefore, kvarBefore, kvarNeeded, capacitorKvar,
    pfAfter, leading, kvaAfter, kvarAfter, ampsAfter, microfaradsWye, microfaradsDelta,
    lossReductionPct: lossReduction * 100, annualKwh, energySavingsPerYear, paybackYears, method,
  };
}

export interface PFRollup {
  motorCount: number;
  kw: number;
  kvarBefore: number;
  kvarAfter: number;
  kvaBefore: number;
  kvaAfter: number;
  pfBefore: number;
  pfAfter: number;
  individualKvar: number;      // Σ standard capacitor sizes, one per motor
  centralBankKvar: number;     // one bank sized from the aggregate to the project target
  energySavingsPerYear: number;
  demandSavingsPerYear: number;
  installedCost: number;
}

/* Utility PF penalties and kVA demand are billed on the whole service, so tariff savings are
   computed here from the aggregate, never summed per motor. Assumes all motors share one service. */
export function calcRollup(motors: PFMotorInput[], s: PFSettings): PFRollup {
  const results = motors.map(m => calcMotor(m, s)).filter(r => r.valid);
  const sum = (f: (r: PFMotorResult) => number) => results.reduce((a, r) => a + f(r), 0);
  const kw = sum(r => r.kw);
  const kvarBefore = sum(r => r.kvarBefore);
  const kvarAfter = sum(r => (r.leading ? -r.kvarAfter : r.kvarAfter));
  const kvaBefore = Math.hypot(kw, kvarBefore);
  const kvaAfter = Math.hypot(kw, kvarAfter);
  const pfBefore = pfFrom(kw, kvarBefore);
  const centralBankKvar = kw > 0 ? kw * kvarFactor(pfBefore, s.targetPf) : 0;
  return {
    motorCount: results.length,
    kw, kvarBefore, kvarAfter: Math.abs(kvarAfter), kvaBefore, kvaAfter,
    pfBefore, pfAfter: pfFrom(kw, Math.abs(kvarAfter)),
    individualKvar: sum(r => r.capacitorKvar ?? r.kvarNeeded),
    centralBankKvar,
    energySavingsPerYear: sum(r => r.energySavingsPerYear),
    demandSavingsPerYear: Math.max(kvaBefore - kvaAfter, 0) * s.demandRatePerKva * 12,
    installedCost: motors.reduce((a, m) => a + (m.installedCost ?? 0), 0),
  };
}
